/**
 * Fleet Dashboard Component
 * =========================
 * Main dashboard view for fleet managers showing fleet health,
 * maintenance alerts, and quick stats.
 */

import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Truck,
  AlertTriangle,
  CheckCircle,
  Clock,
  DollarSign,
  Users,
  Building2,
  Wrench,
  Plus,
  ChevronRight,
  TrendingUp,
  TrendingDown,
  Activity,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { fleetApi } from '../../services/fleetApi';
import PageHeader from '../common/PageHeader';

// Stat Card Component. Was a shadowed card with a 4px ink rule down its left
// edge and a different tint behind every icon; now a hairline card, the
// value in tabular mono, and colour reserved for what it means (overdue).
const StatCard = ({ title, value, subtitle, icon: Icon, trend, trendDirection, color = 'neutral' }) => {
  const iconClasses = {
    neutral: 'bg-surface-sunken text-ink-soft',
    green: 'bg-success-tint text-success',
    red: 'bg-danger-tint text-danger',
  };

  return (
    <div className="bg-surface-card rounded-card border border-border-default p-5">
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <p className="text-label uppercase text-ink-muted">{title}</p>
          <p className="mt-2 text-[1.75rem] leading-tight font-mono tabular-nums font-semibold text-ink">{value}</p>
          {subtitle && (
            <p className="mt-1 text-small text-ink-muted">{subtitle}</p>
          )}
          {trend && (
            <div className={`mt-2 flex items-center text-small ${
              trendDirection === 'up' ? 'text-success' : 'text-danger'
            }`}>
              {trendDirection === 'up' ? (
                <TrendingUp className="w-4 h-4 mr-1" strokeWidth={1.5} aria-hidden="true" />
              ) : (
                <TrendingDown className="w-4 h-4 mr-1" strokeWidth={1.5} aria-hidden="true" />
              )}
              <span className="font-mono tabular-nums">{trend}</span>
            </div>
          )}
        </div>
        <div className={`p-2.5 rounded-control ${iconClasses[color] || iconClasses.neutral}`}>
          <Icon className="w-5 h-5" strokeWidth={1.5} aria-hidden="true" />
        </div>
      </div>
    </div>
  );
};

// Health Score Gauge
const HealthScoreGauge = ({ score }) => {
  const getColor = (s) => {
    if (s >= 80) return 'text-success';
    if (s >= 60) return 'text-warning';
    return 'text-danger';
  };

  const getLabel = (s) => {
    if (s >= 80) return 'Excellent';
    if (s >= 60) return 'Good';
    if (s >= 40) return 'Fair';
    return 'Needs Attention';
  };

  return (
    <div className="bg-surface-card rounded-xl border border-border-default p-6">
      <h3 className="text-label uppercase text-ink-muted mb-4">Fleet Health Score</h3>
      <div className="flex items-center justify-center">
        <div className="relative w-32 h-32">
          <svg className="w-32 h-32 transform -rotate-90">
            <circle
              cx="64"
              cy="64"
              r="56"
              stroke="#e5e7eb"
              strokeWidth="12"
              fill="none"
            />
            <circle
              cx="64"
              cy="64"
              r="56"
              stroke="currentColor"
              strokeWidth="12"
              fill="none"
              strokeDasharray={`${(score / 100) * 352} 352`}
              className={getColor(score)}
            />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className={`text-3xl font-mono tabular-nums font-semibold ${getColor(score)}`}>{score}</span>
            <span className="text-small text-ink-muted">{getLabel(score)}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Maintenance Alert Item
const MaintenanceAlertItem = ({ alert }) => {
  const urgencyConfig = {
    overdue: {
      bg: 'bg-danger-tint',
      border: 'border-danger-border',
      icon: AlertTriangle,
      iconColor: 'text-danger',
      badge: 'bg-danger-tint text-danger',
    },
    due_soon: {
      bg: 'bg-warning-tint',
      border: 'border-warning-border',
      icon: Clock,
      iconColor: 'text-warning',
      badge: 'bg-warning-tint text-warning',
    },
    upcoming: {
      bg: 'bg-accent/10',
      border: 'border-accent',
      icon: Wrench,
      iconColor: 'text-accent',
      badge: 'bg-accent/10 text-accent',
    },
  };

  const config = urgencyConfig[alert.urgency] || urgencyConfig.upcoming;
  const Icon = config.icon;

  return (
    <div className={`${config.bg} ${config.border} border rounded-lg p-4 mb-3`}>
      <div className="flex items-start">
        <div className={`${config.iconColor} mt-0.5`}>
          <Icon className="w-5 h-5" />
        </div>
        <div className="ml-3 flex-1">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-ink-soft">
              {alert.unit_number || 'Unassigned'} - {alert.vehicle_display_name}
            </p>
            <span className={`px-2 py-1 text-small font-medium rounded-full ${config.badge}`}>
              {alert.urgency === 'overdue' 
                ? `${Math.abs(alert.days_overdue)} days overdue`
                : alert.urgency === 'due_soon'
                ? 'Due soon'
                : `In ${alert.days_overdue} days`}
            </span>
          </div>
          <p className="mt-1 text-sm text-ink-muted">
            {alert.service_type}: {alert.service_description}
          </p>
        </div>
        <Link
          to={`/fleet/vehicles/${alert.fleet_vehicle_id}`}
          className="ml-3 text-ink-muted hover:text-ink-muted"
        >
          <ChevronRight className="w-5 h-5" />
        </Link>
      </div>
    </div>
  );
};

// Vehicle Status Distribution
const VehicleStatusChart = ({ stats }) => {
  const total = stats.total_vehicles || 1;
  const segments = [
    { label: 'Active', count: stats.active_vehicles, color: 'bg-success' },
    { label: 'Maintenance', count: stats.vehicles_in_maintenance, color: 'bg-warning' },
    { label: 'Out of Service', count: stats.vehicles_out_of_service, color: 'bg-danger' },
  ];

  return (
    <div className="bg-surface-card rounded-xl border border-border-default p-6">
      <h3 className="text-label uppercase text-ink-muted mb-4">Vehicle Status</h3>
      <div className="h-4 rounded-full bg-surface-muted overflow-hidden flex">
        {segments.map((segment, i) => (
          <div
            key={i}
            className={`${segment.color} transition-all`}
            style={{ width: `${(segment.count / total) * 100}%` }}
          />
        ))}
      </div>
      <div className="mt-4 grid grid-cols-3 gap-4">
        {segments.map((segment, i) => (
          <div key={i} className="text-center">
            <div className="flex items-center justify-center mb-1">
              <div className={`w-3 h-3 rounded-full ${segment.color} mr-2`} />
              <span className="text-sm text-ink-muted">{segment.label}</span>
            </div>
            <p className="text-xl font-mono tabular-nums font-semibold text-ink">{segment.count}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

// Main Dashboard Component
const FleetDashboard = () => {
  const { fleetId } = useParams();

  // Fetch dashboard data
  const { data: stats, isLoading, error } = useQuery({
    queryKey: ['fleetDashboard', fleetId],
    queryFn: () => fleetApi.getDashboard(fleetId),
    refetchInterval: 60000, // Refresh every minute
  });

  // Fetch upcoming maintenance
  const { data: maintenance } = useQuery({
    queryKey: ['fleetMaintenance', fleetId],
    queryFn: () => fleetApi.getUpcomingMaintenance(fleetId, { days_ahead: 30 }),
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-danger-tint border border-danger-border rounded-lg p-4">
        <p className="text-danger">Error loading dashboard: {error.message}</p>
      </div>
    );
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
    }).format(amount);
  };

  return (
    <div>
      <div className="max-w-7xl mx-auto">
        <PageHeader
          title={stats?.fleet_name}
          subtitle={
            <>
              {stats?.subscription_tier.charAt(0).toUpperCase() + stats?.subscription_tier.slice(1)} Plan
              {stats?.vehicle_limit && (
                <>
                  {' • '}
                  <span className="font-mono tabular-nums">{stats.total_vehicles}/{stats.vehicle_limit}</span> vehicles
                </>
              )}
            </>
          }
          action={
            <Link
              to={`/fleet/${fleetId}/vehicles/add`}
              className="inline-flex items-center justify-center gap-2 min-h-touch px-4 rounded-control bg-accent text-white text-body font-display font-semibold hover:bg-accent-hover transition-colors duration-state focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2"
            >
              <Plus size={18} strokeWidth={1.5} aria-hidden="true" />
              Add Vehicle
            </Link>
          }
        />
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto">
        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatCard
            title="Total Vehicles"
            value={stats?.total_vehicles || 0}
            subtitle={`${stats?.active_vehicles || 0} active`}
            icon={Truck}
          />
          <StatCard
            title="Maintenance Due"
            value={(maintenance?.overdue_count || 0) + (maintenance?.due_this_week || 0)}
            subtitle={`${maintenance?.overdue_count || 0} overdue`}
            icon={Wrench}
            color={maintenance?.overdue_count > 0 ? 'red' : 'green'}
          />
          <StatCard
            title="Monthly Spend"
            value={formatCurrency(stats?.cost_this_month || 0)}
            subtitle={`YTD: ${formatCurrency(stats?.cost_ytd || 0)}`}
            icon={DollarSign}
          />
          <StatCard
            title="Drivers"
            value={stats?.total_drivers || 0}
            subtitle={`${stats?.total_departments || 0} departments`}
            icon={Users}
          />
        </div>

        {/* Second Row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          <HealthScoreGauge score={stats?.fleet_health_score || 100} />
          <div className="lg:col-span-2">
            <VehicleStatusChart stats={stats || {}} />
          </div>
        </div>

        {/* Maintenance Alerts */}
        <div className="bg-surface-card rounded-xl border border-border-default p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-section-title font-display text-ink">Maintenance Alerts</h2>
            <Link
              to={`/fleet/${fleetId}/maintenance`}
              className="text-sm text-accent hover:text-accent-hover font-medium"
            >
              View all
            </Link>
          </div>

          {maintenance?.alerts?.length > 0 ? (
            <div>
              {maintenance.alerts.slice(0, 5).map((alert, i) => (
                <MaintenanceAlertItem key={i} alert={alert} />
              ))}
              {maintenance.alerts.length > 5 && (
                <p className="text-sm text-ink-muted text-center mt-4">
                  +{maintenance.alerts.length - 5} more alerts
                </p>
              )}
            </div>
          ) : (
            <div className="text-center py-8">
              <CheckCircle className="w-12 h-12 text-success mx-auto mb-3" />
              <p className="text-ink-muted">All vehicles are up to date on maintenance!</p>
            </div>
          )}
        </div>

        {/* Quick Links */}
        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
          <Link
            to={`/fleet/${fleetId}/vehicles`}
            className="bg-surface-card rounded-lg border border-border-default p-4 transition-colors flex items-center hover:border-border-strong"
          >
            <Truck className="w-8 h-8 text-accent mr-3" />
            <div>
              <p className="font-medium text-ink-soft">Vehicles</p>
              <p className="text-sm text-ink-muted">Manage fleet</p>
            </div>
          </Link>
          <Link
            to={`/fleet/${fleetId}/drivers`}
            className="bg-surface-card rounded-lg border border-border-default p-4 transition-colors flex items-center hover:border-border-strong"
          >
            <Users className="w-8 h-8 text-success mr-3" />
            <div>
              <p className="font-medium text-ink-soft">Drivers</p>
              <p className="text-sm text-ink-muted">Manage drivers</p>
            </div>
          </Link>
          <Link
            to={`/fleet/${fleetId}/departments`}
            className="bg-surface-card rounded-lg border border-border-default p-4 transition-colors flex items-center hover:border-border-strong"
          >
            <Building2 className="w-8 h-8 text-info mr-3" />
            <div>
              <p className="font-medium text-ink-soft">Departments</p>
              <p className="text-sm text-ink-muted">Cost centers</p>
            </div>
          </Link>
          <Link
            to={`/fleet/${fleetId}/reports`}
            className="bg-surface-card rounded-lg border border-border-default p-4 transition-colors flex items-center hover:border-border-strong"
          >
            <Activity className="w-8 h-8 text-warning mr-3" />
            <div>
              <p className="font-medium text-ink-soft">Reports</p>
              <p className="text-sm text-ink-muted">Cost analysis</p>
            </div>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default FleetDashboard;
